import { format, isValid } from 'date-fns'
import qs from 'qs'
import { axiosGet } from './api'
import { parseDate } from './dates'

export type AdManagerRead = Readonly<{ endpoint: string; query: string }>

type CalendarDateParameter = Date | string | null | undefined

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/

const formatLocalDate = (date: Date): string => {
    if (!isValid(date)) {
        throw new Error('Invalid Ad Manager calendar date')
    }

    return format(date, 'yyyy-MM-dd')
}

/**
 * Convert a performance request date to a calendar date without changing its
 * meaning to UTC. Performance ranges are dates on the user's calendar, not
 * instants in time.
 */
export const calendarDateParam = (value: CalendarDateParameter): CalendarDateParameter => {
    if (value === null || value === undefined || value === '') {
        return value
    }

    if (value instanceof Date) {
        return formatLocalDate(value)
    }

    if (DATE_ONLY.test(value)) {
        return formatLocalDate(parseDate(value))
    }

    return formatLocalDate(new Date(value))
}

export const normalizeAdManagerPerformanceParams = <T extends Record<string, any>>(
    params: T | null | undefined,
): T | null | undefined => {
    if (params === null || params === undefined) {
        return params
    }

    const normalized: Record<string, any> = { ...params }
    for (const key of ['from', 'to'] as const) {
        if (Object.hasOwn(normalized, key)) {
            if (
                normalized[key] === null ||
                normalized[key] === undefined ||
                normalized[key] === ''
            ) {
                delete normalized[key]
            } else {
                normalized[key] = calendarDateParam(normalized[key])
            }
        }
    }
    return normalized as T
}

export const adManagerPerformanceGet = (
    endpoint: string,
    params: Record<string, any> | null | undefined = null,
    raw = false,
): Promise<any> => {
    const normalizedParams = normalizeAdManagerPerformanceParams(params)
    // URL-selected serving is deliberately transient request context: it is
    // forwarded only to Ad Manager reads, never persisted in a table/recipe.
    const backend = new URLSearchParams(window.location.search).get('backend')
    // Only the unified routes serve `parquet`; a classic route answers 400 for it (MER-3667).
    const forward =
        backend !== null && (backend !== 'parquet' || endpoint.startsWith('api/v5/unified/'))
    const requestParams = forward ? { ...(normalizedParams ?? {}), backend } : normalizedParams
    const adManagerRead: AdManagerRead = { endpoint, query: qs.stringify(requestParams) }
    const request = raw
        ? axiosGet(endpoint, requestParams, true)
        : axiosGet(endpoint, requestParams)
    return request.then((response) => (response ? { ...response, adManagerRead } : response))
}
